/* eslint-disable max-lines */

/* Sorting
 *
 * - Public API
 *   - event order from public api documentation
 *   - event property order from public api documentation
 * - Internal API
 *   - event order from internal api documentation
 *   - event property order from internal api documentation
 * - Undocumented API
 *   - event order alphabetically
 *   - event property order
 *     - id properties first alphabetically
 *     - rest of properties alphabetically
 */

import {
    NinchatAttributes,
    NinchatMessages,
    NinchatProperties,
    NinchatSettings,
} from './'
import {FileID} from './properties'

export enum NinchatEventType {
    // Public API
    NinchatErrorEvent = 'error',
    NinchatSessionCreatedEvent = 'session_created',
    NinchatSessionUpdatedEvent = 'session_updated',
    NinchatSessionStatusUpdatedEvent = 'session_status_updated',
    NinchatUserCreatedEvent = 'user_created',
    NinchatUserFoundEvent = 'user_found',
    NinchatUserUpdatedEvent = 'user_updated',
    NinchatUserDeletedEvent = 'user_deleted',
    NinchatIdentityFoundEvent = 'identity_found',
    NinchatIdentityCreatedEvent = 'identity_created',
    NinchatIdentityUpdatedEvent = 'identity_updated',
    NinchatIdentityDeletedEvent = 'identity_deleted',
    NinchatDialogueUpdatedEvent = 'dialogue_updated',
    NinchatChannelCreatedEvent = 'channel_created',
    NinchatChannelFoundEvent = 'channel_found',
    NinchatChannelJoinedEvent = 'channel_joined',
    NinchatChannelPartedEvent = 'channel_parted',
    NinchatChannelUpdatedEvent = 'channel_updated',
    NinchatChannelDeletedEvent = 'channel_deleted',
    NinchatChannelMemberJoinedEvent = 'channel_member_joined',
    NinchatChannelMemberPartedEvent = 'channel_member_parted',
    NinchatChannelMemberUpdatedEvent = 'channel_member_updated',
    NinchatRealmFoundEvent = 'realm_found',
    NinchatRealmQueuesFoundEvent = 'realm_queues_found',
    NinchatRealmSitesFoundEvent = 'realm_sites_found',
    NinchatSiteFoundEvent = 'site_found',
    NinchatRealmJoinedEvent = 'realm_joined',
    NinchatRealmPartedEvent = 'realm_parted',
    NinchatRealmUpdatedEvent = 'realm_updated',
    NinchatRealmDeletedEvent = 'realm_deleted',
    NinchatRealmMemberJoinedEvent = 'realm_member_joined',
    NinchatRealmMemberPartedEvent = 'realm_member_parted',
    NinchatRealmMemberUpdatedEvent = 'realm_member_updated',
    NinchatQueueCreatedEvent = 'queue_created',
    NinchatQueueFoundEvent = 'queue_found',
    NinchatQueueUpdatedEvent = 'queue_updated',
    NinchatQueueDeletedEvent = 'queue_deleted',
    NinchatQueueJoinedEvent = 'queue_joined',
    NinchatQueuePartedEvent = 'queue_parted',
    NinchatQueueMemberJoinedEvent = 'queue_member_joined',
    NinchatQueueMemberPartedEvent = 'queue_member_parted',
    NinchatQueueMetadataContents = 'queue_metadata_contents',
    NinchatAudienceEnqueuedEvent = 'audience_enqueued',
    NinchatMessageReceivedEvent = 'message_received',
    NinchatMessageUpdatedEvent = 'message_updated',
    NinchatHistoryResultsEvent = 'history_results',
    NinchatHistoryDiscardedEvent = 'history_discarded',
    NinchatAccessFoundEvent = 'access_found',
    NinchatAccessCreatedEvent = 'access_created',
    NinchatMasterKeysFoundEvent = 'master_keys_found',
    NinchatMasterKeyCreatedEvent = 'master_key_created',
    NinchatMasterKeyDeletedEvent = 'master_key_deleted',
    NinchatFileFoundEvent = 'file_found',
    NinchatFileUploadedEvent = 'file_uploaded',
    NinchatSiteCreatedEvent = 'site_created',
    NinchatSiteUpdatedEvent = 'site_updated',
    NinchatTranscriptContentsEvent = 'transcript_contents',
    NinchatTranscriptDeletedEvent = 'transcript_deleted',
    NinchatQueueTranscriptsFoundEvent = 'queue_transcripts_found',
    NinchatQueueTranscriptsDeletedEvent = 'queue_transcripts_deleted',
    NinchatTagCreatedEvent = 'tag_created',
    NinchatTagFoundEvent = 'tag_found',
    NinchatTagsFoundEvent = 'tags_found',
    NinchatTagUpdatedEvent = 'tag_updated',
    NinchatTagDeletedEvent = 'tag_deleted',
    NinchatQueueStatsContentsEvent = 'queue_stats_contents',
    NinchatIceBegunEvent = 'ice_begun',
    NinchatSearchResultsEvent = 'search_results',
    NinchatPongEvent = 'pong',
    NinchatAckEvent = 'ack',
    // Internal API
    NinchatCommentCreatedEvent = 'comment_created',
    NinchatAudienceRangeCommentContentsEvent = 'audience_range_comment_contents',
    NinchatAudienceRegisteredEvent = 'audience_registered',
    NinchatSsoBegunEvent = 'sso_begun',
    // Undocumented API
    NinchatJitsiDiscoveredEvent = 'jitsi_discovered',
    NinchatQueueActivityEvent = 'queue_activity',
    NinchatTranscriptFoundEvent = 'transcript_found', // https://github.com/somia/nin/blob/b29bae1cfb773d7449ac8d19771d629fb86ee7a2/src/logic/events.py#L705
    NinchatTranscriptUpdatedEvent = 'transcript_updated', // https://github.com/somia/nin/blob/b29bae1cfb773d7449ac8d19771d629fb86ee7a2/src/logic/events.py#L715
    NinchatPermissionGroupsEvent = 'permission_groups_listed',
    NinchatPermissionGroupCreatedEvent = 'permission_group_created',
    NinchatPermissionGroupUpdatedEvent = 'permission_group_updated'
}

/* Base */

interface NinchatEvents {
    event: NinchatProperties.EventType
    event_id?: NinchatProperties.EventID
    frames?: NinchatProperties.Count
}

export interface NinchatGenericEventProperties extends NinchatEvents {
    action_id?: NinchatProperties.ActionID
}

/* Events */

/* Public API
 * - event order from public api documentation
 * - event property order from public api documentation
 */

export interface NinchatErrorEvent extends NinchatEvents {
    event: 'error'
    error_type: NinchatProperties.ErrorType
    error_reason?: NinchatProperties.ErrorReason
    session_id?: NinchatProperties.SessionID
    action_id?: NinchatProperties.ActionID
    user_id?: NinchatProperties.UserID
    identity_type?: NinchatProperties.IdentityType
    identity_name?: NinchatProperties.IdentityName
    channel_id?: NinchatProperties.ChannelID
    realm_id?: NinchatProperties.RealmID
    queue_id?: NinchatProperties.QueueID
    tag_id?: NinchatProperties.TagID
    message_type?: NinchatMessages.NinchatMessageType
    plan_id?: NinchatProperties.PlanID // internal
}

export interface NinchatFileUploadedEvent extends NinchatEvents {
    event: 'file_uploaded'
    file_id: NinchatProperties.FileID
}

export interface NinchatSessionCreatedEvent extends NinchatEvents {
    event: 'session_created'
    session_id: NinchatProperties.SessionID
    session_host?: NinchatProperties.SessionHost
    user_id: NinchatProperties.UserID
    user_auth?: NinchatProperties.UserAuth
    user_attrs: NinchatAttributes.NinchatUserAttributes
    user_settings: NinchatSettings.NinchatUserSettings
    user_account: NinchatProperties.NinchatUserAccount
    user_identities: NinchatProperties.NinchatUserIdentities
    user_dialogues: NinchatProperties.NinchatUserDialogues
    user_channels: NinchatProperties.NinchatUserChannels
    user_realms: {[key: NinchatProperties.RealmID]: NinchatAttributes.NinchatRealmAttributes}
    user_realms_member?: NinchatProperties.NinchatUserRealmsMember
    session_realms_permissions: Record<NinchatProperties.RealmID, Array<NinchatProperties.PermissionTypes>>
    user_queues?: NinchatProperties.NinchatUserQueues
    user_transcripts?: NinchatProperties.NinchatUserTranscripts // undocumented
    puppet_masters?: NinchatProperties.NinchatPuppetMasters
    user_sso: NinchatProperties.NinchatUserSso // internal
}

export interface NinchatSessionUpdatedEvent extends NinchatEvents {
    event: 'session_updated'
    session_realms_permissions: Record<NinchatProperties.RealmID, Array<NinchatProperties.PermissionTypes>>
}

export interface NinchatSessionStatusUpdatedEvent extends NinchatEvents {
    event: 'session_status_updated'
    channel_id?: NinchatProperties.ChannelID
    user_id?: NinchatProperties.UserID
    message_id: NinchatProperties.MessageID
}

export interface NinchatUserCreatedEvent extends NinchatEvents {
    event: 'user_created'
    action_id?: NinchatProperties.ActionID
    user_id: NinchatProperties.UserID
    user_auth?: NinchatProperties.UserAuth
    user_attrs: NinchatAttributes.NinchatUserAttributes
    user_settings: NinchatSettings.NinchatUserSettings
    puppet_masters?: NinchatProperties.NinchatPuppetMasters
}

export interface NinchatUserFoundEvent extends NinchatEvents {
    event: 'user_found'
    action_id?: NinchatProperties.ActionID
    user_id: NinchatProperties.UserID
    user_attrs: NinchatAttributes.NinchatUserAttributes
    user_settings?: NinchatSettings.NinchatUserSettings
    user_account?: NinchatProperties.NinchatUserAccount
    user_identities: NinchatProperties.NinchatUserIdentities
    user_dialogues?: NinchatProperties.NinchatUserDialogues
    user_channels?: NinchatProperties.NinchatUserChannels
    user_realms?: {[key: NinchatProperties.RealmID]: NinchatAttributes.NinchatRealmAttributes}
    user_realms_member?: NinchatProperties.NinchatUserRealmsMember
    user_queues?: NinchatProperties.NinchatUserQueues
    user_transcripts?: NinchatProperties.NinchatUserTranscripts // undocumented
    dialogue_members?: NinchatProperties.NinchatDialogueMembers
    dialogue_status?: NinchatProperties.DialogueStatus
    message_time?: NinchatProperties.Time
    audience_metadata?: NinchatProperties.NinchatAudienceMetadata
    puppet_masters?: NinchatProperties.NinchatPuppetMasters
    user_sso?: NinchatProperties.NinchatUserSso // internal
}

export interface NinchatUserUpdatedEvent extends NinchatEvents {
    event: 'user_updated'
    action_id?: NinchatProperties.ActionID
    user_id: NinchatProperties.UserID
    user_attrs: NinchatAttributes.NinchatUserAttributes
    user_settings?: NinchatSettings.NinchatUserSettings
    user_account?: NinchatProperties.NinchatUserAccount
    puppet_masters?: NinchatProperties.NinchatPuppetMasters
    user_sso?: NinchatProperties.NinchatUserSso // internal
}

export interface NinchatUserDeletedEvent extends NinchatEvents {
    event: 'user_deleted'
    action_id?: NinchatProperties.ActionID
    user_id: NinchatProperties.UserID
}

export interface NinchatIdentityFoundEvent extends NinchatEvents {
    event: 'identity_found'
    action_id?: NinchatProperties.ActionID
    user_id: NinchatProperties.UserID
    identity_type: NinchatProperties.IdentityType
    identity_name: NinchatProperties.IdentityName
    identity_attrs?: NinchatAttributes.NinchatIdentityAttributes
}

export interface NinchatIdentityCreatedEvent extends NinchatEvents {
    event: 'identity_created'
    action_id?: NinchatProperties.ActionID
    user_id: NinchatProperties.UserID
    identity_type: NinchatProperties.IdentityType
    identity_name: NinchatProperties.IdentityName
    identity_attrs: NinchatAttributes.NinchatIdentityAttributes
}

export interface NinchatIdentityUpdatedEvent extends NinchatEvents {
    event: 'identity_updated'
    action_id?: NinchatProperties.ActionID
    user_id: NinchatProperties.UserID
    identity_type: NinchatProperties.IdentityType
    identity_name: NinchatProperties.IdentityName
    identity_attrs: NinchatAttributes.NinchatIdentityAttributes
    user_auth?: NinchatProperties.UserAuth // internal
}

export interface NinchatIdentityDeletedEvent extends NinchatEvents {
    event: 'identity_deleted'
    action_id?: NinchatProperties.ActionID
    user_id: NinchatProperties.UserID
    identity_type: NinchatProperties.IdentityType
    identity_name: NinchatProperties.IdentityName
}

export interface NinchatDialogueUpdatedEvent extends NinchatEvents {
    event: 'dialogue_updated'
    action_id?: NinchatProperties.ActionID
    user_id: NinchatProperties.UserID
    dialogue_members: NinchatProperties.NinchatDialogueMembers
    dialogue_status?: NinchatProperties.DialogueStatus
    message_time?: NinchatProperties.Time
    audience_metadata?: NinchatProperties.NinchatAudienceMetadata
    dialogue_metadata?: NinchatProperties.NinchatDialogueMetadata
}

export interface NinchatChannelFoundEvent extends NinchatEvents {
    event: 'channel_found'
    action_id?: NinchatProperties.ActionID
    channel_id: NinchatProperties.ChannelID
    channel_attrs: NinchatAttributes.NinchatChannelAttributes
    channel_members?: NinchatProperties.NinchatChannelMembers
    channel_members_metadata?: NinchatProperties.NinchatChannelMembersMetadata
    channel_member_count?: NinchatProperties.Count
    channel_status?: NinchatProperties.ChannelStatus
    message_time?: NinchatProperties.Time
    realm_id?: NinchatProperties.RealmID
    audience_metadata?: NinchatProperties.NinchatAudienceMetadata
    channel_metadata?: NinchatProperties.NinchatChannelMetadata
    seen_message_id?: NinchatProperties.MessageID // undocumented
}

export interface NinchatChannelJoinedEvent extends NinchatEvents {
    event: 'channel_joined'
    action_id?: NinchatProperties.ActionID
    channel_id: NinchatProperties.ChannelID
    channel_attrs: NinchatAttributes.NinchatChannelAttributes
    channel_members: NinchatProperties.NinchatChannelMembers
    channel_members_metadata?: NinchatProperties.NinchatChannelMembersMetadata
    message_time?: NinchatProperties.Time
    realm_id?: NinchatProperties.RealmID
    audience_metadata?: NinchatProperties.NinchatAudienceMetadata
    channel_metadata?: NinchatProperties.NinchatChannelMetadata
}

export interface NinchatChannelPartedEvent extends NinchatEvents {
    event: 'channel_parted'
    action_id?: NinchatProperties.ActionID
    event_cause?: NinchatProperties.EventCause
    channel_id: NinchatProperties.ChannelID
}

export interface NinchatChannelUpdatedEvent extends NinchatEvents {
    event: 'channel_updated'
    action_id?: NinchatProperties.ActionID
    event_cause?: NinchatProperties.EventCause
    channel_id: NinchatProperties.ChannelID
    channel_attrs: NinchatAttributes.NinchatChannelAttributes
    realm_id?: NinchatProperties.RealmID
}

export interface NinchatChannelDeletedEvent extends NinchatEvents {
    event: 'channel_deleted'
    action_id?: NinchatProperties.ActionID
    channel_id: NinchatProperties.ChannelID
}

export interface NinchatChannelMemberJoinedEvent extends NinchatEvents {
    event: 'channel_member_joined'
    channel_id: NinchatProperties.ChannelID
    user_id: NinchatProperties.UserID
    user_attrs: NinchatAttributes.NinchatUserAttributes
    member_attrs: NinchatAttributes.NinchatChannelMemberAttributes
    member_metadata?: NinchatProperties.NinchatChannelMembersMetadata
    puppet_attrs?: NinchatAttributes.NinchatPuppetAttributes
}

export interface NinchatChannelMemberPartedEvent extends NinchatEvents {
    event: 'channel_member_parted'
    action_id?: NinchatProperties.ActionID
    event_cause?: NinchatProperties.EventCause
    channel_id: NinchatProperties.ChannelID
    user_id: NinchatProperties.UserID
}

export interface NinchatChannelMemberUpdatedEvent extends NinchatEvents {
    event: 'channel_member_updated'
    action_id?: NinchatProperties.ActionID
    channel_id: NinchatProperties.ChannelID
    user_id: NinchatProperties.UserID
    member_attrs: NinchatAttributes.NinchatChannelMemberAttributes
}

export interface NinchatRealmFoundEvent extends NinchatEvents {
    event: 'realm_found'
    action_id?: NinchatProperties.ActionID
    realm_id: NinchatProperties.RealmID
    realm_attrs: NinchatAttributes.NinchatRealmAttributes
    realm_settings: NinchatSettings.NinchatRealmSettings
    realm_members?: NinchatProperties.NinchatRealmMembers
}

export interface NinchatRealmQueuesFoundEvent extends NinchatEvents {
    event: 'realm_queues_found'
    action_id: NinchatProperties.ActionID
    realm_id: NinchatProperties.RealmID
    realm_queues: NinchatProperties.NinchatRealmQueues
}

export interface NinchatRealmSitesFoundEvent extends NinchatEvents {
    event: 'realm_sites_found'
    action_id: NinchatProperties.ActionID
    realm_id: NinchatProperties.RealmID
    realm_sites: NinchatProperties.NinchatRealmSites
}

export interface NinchatSiteFoundEvent extends NinchatEvents {
    event: 'site_found'
    action_id: NinchatProperties.ActionID
    realm_id: NinchatProperties.RealmID
    site_id: NinchatProperties.SiteId
    site_attrs: NinchatAttributes.NinchatSiteAttributes
}

export interface NinchatSiteUpdatedEvent extends NinchatEvents {
    event: 'site_updated',
    action_id: NinchatProperties.ActionID
    realm_id: NinchatProperties.RealmID
    site_id: NinchatProperties.SiteId
    site_attrs: NinchatAttributes.NinchatSiteAttributes
}

export interface NinchatSiteCreatedEvent extends NinchatEvents {
    event: 'site_created'
    action_id: NinchatProperties.ActionID
    realm_id: NinchatProperties.RealmID
    site_id: NinchatProperties.SiteId
    site_attrs: NinchatAttributes.NinchatSiteAttributes
}


export interface NinchatRealmJoinedEvent extends NinchatEvents {
    event: 'realm_joined'
    action_id?: NinchatProperties.ActionID
    realm_id: NinchatProperties.RealmID
    realm_attrs: NinchatAttributes.NinchatRealmAttributes
    realm_settings: Record<string, unknown>
    realm_members: NinchatProperties.NinchatRealmMembers
}

export interface NinchatRealmPartedEvent extends NinchatEvents {
    event: 'realm_parted'
    action_id?: NinchatProperties.ActionID
    event_cause?: NinchatProperties.EventCause
    realm_id: NinchatProperties.RealmID
}

export interface NinchatRealmUpdatedEvent extends NinchatEvents {
    event: 'realm_updated'
    action_id?: NinchatProperties.ActionID
    realm_id: NinchatProperties.RealmID
    realm_attrs: NinchatAttributes.NinchatRealmAttributes
    realm_settings: NinchatSettings.NinchatRealmSettings
}

export interface NinchatRealmDeletedEvent extends NinchatEvents {
    event: 'realm_deleted'
    action_id?: NinchatProperties.ActionID
    realm_id: NinchatProperties.RealmID
}

export interface NinchatRealmMemberJoinedEvent extends NinchatEvents {
    event: 'realm_member_joined'
    action_id?: NinchatProperties.ActionID
    realm_id: NinchatProperties.RealmID
    user_id: NinchatProperties.UserID
    user_attrs: NinchatAttributes.NinchatUserAttributes
    member_attrs: NinchatAttributes.NinchatRealmMemberAttributes
    puppet_attrs?: NinchatAttributes.NinchatPuppetAttributes
}

export interface NinchatRealmMemberPartedEvent extends NinchatEvents {
    event: 'realm_member_parted'
    action_id?: NinchatProperties.ActionID
    event_cause?: NinchatProperties.EventCause
    realm_id: NinchatProperties.RealmID
    user_id: NinchatProperties.UserID
}

export interface NinchatRealmMemberUpdatedEvent extends NinchatEvents {
    event: 'realm_member_updated'
    action_id?: NinchatProperties.ActionID
    realm_id: NinchatProperties.RealmID
    user_id: NinchatProperties.UserID
    member_attrs: NinchatAttributes.NinchatRealmMemberAttributes
}

export interface NinchatQueueCreatedEvent extends NinchatEvents {
    event: 'queue_created'
    action_id?: NinchatProperties.ActionID
    queue_id: NinchatProperties.QueueID
    queue_attrs: NinchatAttributes.NinchatQueueAttributes
    queue_members?: NinchatProperties.NinchatQueueMembers
    queue_settings: NinchatSettings.NinchatQueueSettings
    realm_id?: NinchatProperties.RealmID
}

export interface NinchatChannelCreatedEvent extends NinchatEvents {
    event: 'channel_created'
    action_id?: NinchatProperties.ActionID
    channel_id: NinchatProperties.ChannelID
    channel_attrs: NinchatAttributes.NinchatChannelAttributes
    realm_id?: NinchatProperties.RealmID
}

export interface NinchatQueueFoundEvent extends NinchatEvents {
    event: 'queue_found'
    action_id?: NinchatProperties.ActionID
    queue_id: NinchatProperties.QueueID
    queue_attrs: NinchatAttributes.NinchatQueueAttributes
    queue_requests?: NinchatProperties.NinchatQueueRequest[]
    queue_settings: NinchatSettings.NinchatQueueSettings
    queue_members?: NinchatProperties.NinchatQueueMembers
    queue_position?: NinchatProperties.Count
    queue_time?: NinchatProperties.Time // undocumented
    realm_id?: NinchatProperties.RealmID
    queue_sso?: NinchatProperties.NinchatQueueSso // internal
}

export interface NinchatQueueUpdatedEvent extends NinchatEvents {
    event: 'queue_updated'
    action_id?: NinchatProperties.ActionID
    event_cause?: NinchatProperties.EventCause
    queue_id: NinchatProperties.QueueID
    queue_attrs: NinchatAttributes.NinchatQueueAttributes
    queue_request?: NinchatProperties.NinchatQueueRequest
    queue_settings: NinchatSettings.NinchatQueueSettings
    queue_position?: NinchatProperties.Count
    queue_time?: NinchatProperties.Time // undocumented
    realm_id?: NinchatProperties.RealmID
    queue_sso?: NinchatProperties.NinchatQueueSso // internal
}

export interface NinchatQueueDeletedEvent extends NinchatEvents {
    event: 'queue_deleted'
    action_id?: NinchatProperties.ActionID
    queue_id: NinchatProperties.QueueID
    realm_id?: NinchatProperties.RealmID
}

export interface NinchatQueueJoinedEvent extends NinchatEvents {
    event: 'queue_joined'
    queue_id: NinchatProperties.QueueID
    queue_attrs: NinchatAttributes.NinchatQueueAttributes
    queue_settings: NinchatSettings.NinchatQueueSettings
    realm_id?: NinchatProperties.RealmID
}

export interface NinchatQueuePartedEvent extends NinchatEvents {
    event: 'queue_parted'
    event_cause?: NinchatProperties.EventCause
    queue_id: NinchatProperties.QueueID
    realm_id?: NinchatProperties.RealmID
}

export interface NinchatQueueMetadataContents extends NinchatEvents {
    event: 'queue_metadata_contents',
    queue_id: NinchatProperties.QueueID
    queue_metadata: NinchatProperties.QueueMetadata
}

export interface NinchatPermissionGroupsEvent extends NinchatEvents {
    event: 'permission_groups_listed',
    permission_groups: NinchatProperties.NinchatPermissionGroups
}

export interface NinchatPermissionGroupCreatedEvent extends NinchatEvents {
    event: 'permission_group_created',
    permission_group_id: string
}

export interface NinchatPermissionGroupUpdatedEvent extends NinchatEvents {
    event: 'permission_group_updated',
    permission_group_id: string
}

export interface NinchatQueueMemberJoinedEvent extends NinchatEvents {
    event: 'queue_member_joined'
    action_id?: NinchatProperties.ActionID
    queue_id: NinchatProperties.QueueID
    user_id: NinchatProperties.UserID
    user_attrs: NinchatAttributes.NinchatUserAttributes
    member_attrs: NinchatAttributes.NinchatQueueMemberAttributes
}

export interface NinchatQueueMemberPartedEvent extends NinchatEvents {
    event: 'queue_member_parted'
    action_id?: NinchatProperties.ActionID
    event_cause?: NinchatProperties.EventCause
    queue_id: NinchatProperties.QueueID
    user_id: NinchatProperties.UserID
}

export interface NinchatAudienceEnqueuedEvent extends NinchatEvents {
    event: 'audience_enqueued'
    action_id?: NinchatProperties.ActionID
    queue_id: NinchatProperties.QueueID
    queue_attrs: NinchatAttributes.NinchatQueueAttributes
    queue_position: NinchatProperties.Count
    audience_id: NinchatProperties.AudienceID
    queue_sso?: NinchatProperties.NinchatQueueSso // internal
}

export interface NinchatMessageReceivedEvent extends NinchatEvents {
    event: 'message_received'
    action_id?: NinchatProperties.ActionID
    channel_id?: NinchatProperties.ChannelID
    user_id?: NinchatProperties.UserID
    message_id: NinchatProperties.MessageID
    message_time: NinchatProperties.Time
    message_type: NinchatMessages.NinchatMessageType
    message_user_id?: NinchatProperties.UserID
    message_user_name?: NinchatProperties.UserName
    message_recipient_ids?: NinchatProperties.UserID[]
    message_hidden?: boolean
    message_fold?: boolean
    message_ttl?: NinchatProperties.Seconds
    history_length?: NinchatProperties.Count
    message_deleted?: boolean // internal
    message_privileged?: boolean // internal
}

export interface NinchatMessageUpdatedEvent extends NinchatEvents {
    event: 'message_updated'
    action_id?: NinchatProperties.ActionID
    channel_id?: NinchatProperties.ChannelID
    message_id: NinchatProperties.MessageID
    message_hidden?: boolean
    message_deleted?: boolean // internal
    user_id?: NinchatProperties.UserID // undocumented
}

export interface NinchatHistoryResultsEvent extends NinchatEvents {
    event: 'history_results'
    action_id: NinchatProperties.ActionID
    channel_id?: NinchatProperties.ChannelID
    user_id?: NinchatProperties.UserID
    history_length: NinchatProperties.Count
    message_id?: NinchatProperties.MessageID
}

export interface NinchatHistoryDiscardedEvent extends NinchatEvents {
    event: 'history_discarded'
    action_id: NinchatProperties.ActionID
    channel_id?: NinchatProperties.ChannelID
    user_id?: NinchatProperties.UserID
    message_id: NinchatProperties.MessageID
}

export interface NinchatAccessFoundEvent extends NinchatEvents {
    event: 'access_found'
    action_id?: NinchatProperties.ActionID
    access_type: NinchatProperties.AccessType
    user_id?: NinchatProperties.UserID
    user_attrs?: NinchatAttributes.NinchatUserAttributes
    identity_type?: NinchatProperties.IdentityType
    identity_name?: NinchatProperties.IdentityName
    channel_id?: NinchatProperties.ChannelID
    channel_attrs?: NinchatAttributes.NinchatChannelAttributes
    channel_unsilence?: boolean
    realm_id?: NinchatProperties.RealmID
    realm_attrs?: NinchatAttributes.NinchatRealmAttributes
    realm_member?: boolean
}

export interface NinchatAccessCreatedEvent extends NinchatEvents {
    event: 'access_created'
    action_id?: NinchatProperties.ActionID
    access_type: NinchatProperties.AccessType
    access_key?: NinchatProperties.AccessKey
}

export interface NinchatMasterKeysFoundEvent extends NinchatEvents {
    event: 'master_keys_found'
    action_id: NinchatProperties.ActionID
    master_keys: NinchatProperties.NinchatMasterKeys
}

export interface NinchatMasterKeyCreatedEvent extends NinchatEvents {
    event: 'master_key_created'
    action_id?: NinchatProperties.ActionID
    realm_id?: NinchatProperties.RealmID
    master_key_attrs?: NinchatProperties.MasterKeyAttrs
    master_key_type: NinchatProperties.MasterKeyType
    master_key_id: NinchatProperties.MasterKeyID
    master_key_secret?: NinchatProperties.MasterKeySecret
}

export interface NinchatMasterKeyDeletedEvent extends NinchatEvents {
    event: 'master_key_deleted'
    action_id?: NinchatProperties.ActionID
    realm_id?: NinchatProperties.RealmID
    master_key_type: NinchatProperties.MasterKeyType
    master_key_id: NinchatProperties.MasterKeyID
}

export interface NinchatFileFoundEvent extends NinchatEvents {
    event: 'file_found'
    action_id?: NinchatProperties.ActionID
    file_id: NinchatProperties.FileID
    file_attrs: NinchatAttributes.NinchatFileAttributes
    file_url: NinchatProperties.FileUrl
    thumbnail_url?: NinchatProperties.ThumbnailUrl
    url_expiry: NinchatProperties.Time
    converted_url?: NinchatProperties.ConvertedUrl
}

export interface NinchatTranscriptContentsEvent extends NinchatEvents {
    event: 'transcript_contents'
    action_id: NinchatProperties.ActionID
    channel_id?: NinchatProperties.ChannelID
    channel_members_metadata?: NinchatProperties.NinchatChannelMembersMetadata
    dialogue_id?: NinchatProperties.UserID[]
    dialogue_members?: NinchatProperties.NinchatDialogueMembers
    audience_id?: NinchatProperties.AudienceID
    audience_members?: NinchatProperties.NinchatAudienceMembers
    audience_metadata?: NinchatProperties.NinchatAudienceMetadata
    transcript_messages?: NinchatProperties.NinchatTranscriptMessage[]
    audience_files?: {[key: FileID]: Pick<NinchatProperties.NinchatFile, 'file_attrs'>}
    message_id?: NinchatProperties.MessageID
}

export interface NinchatTranscriptDeletedEvent extends NinchatEvents {
    event: 'transcript_deleted'
    action_id?: NinchatProperties.ActionID
    channel_id?: NinchatProperties.ChannelID
    dialogue_id?: NinchatProperties.UserID[]
    audience_id?: NinchatProperties.AudienceID
}

export interface NinchatQueueTranscriptsFoundEvent extends NinchatEvents {
    event: 'queue_transcripts_found'
    action_id: NinchatProperties.ActionID
    queue_id: NinchatProperties.QueueID
    queue_transcripts: NinchatProperties.NinchatQueueTranscript[]
}

export interface NinchatQueueTranscriptsDeletedEvent extends NinchatEvents {
    event: 'queue_transcripts_deleted'
    action_id: NinchatProperties.ActionID
    queue_id: NinchatProperties.QueueID
    interval_begin: NinchatProperties.Time
    interval_end: NinchatProperties.Time
}

export interface NinchatTagCreatedEvent extends NinchatEvents {
    event: 'tag_created'
    action_id?: NinchatProperties.ActionID
    realm_id?: NinchatProperties.RealmID
    tag_id: NinchatProperties.TagID
    tag_attrs: NinchatAttributes.NinchatTagAttributes
}

export interface NinchatTagFoundEvent extends NinchatEvents {
    event: 'tag_found'
    action_id?: NinchatProperties.ActionID
    realm_id?: NinchatProperties.RealmID
    tag_id: NinchatProperties.TagID
    tag_attrs: NinchatAttributes.NinchatTagAttributes
}

export interface NinchatTagsFoundEvent extends NinchatEvents {
    event: 'tags_found'
    action_id?: NinchatProperties.ActionID
    realm_id?: NinchatProperties.RealmID
    tag_id?: NinchatProperties.TagID
    tag_attrs?: NinchatAttributes.NinchatTagAttributes
    tag_children: NinchatProperties.NinchatTagChildren
}

export interface NinchatTagUpdatedEvent extends NinchatEvents {
    event: 'tag_updated'
    action_id?: NinchatProperties.ActionID
    realm_id?: NinchatProperties.RealmID
    tag_id: NinchatProperties.TagID
    tag_attrs: NinchatAttributes.NinchatTagAttributes
}

export interface NinchatTagDeletedEvent extends NinchatEvents {
    event: 'tag_deleted'
    action_id?: NinchatProperties.ActionID
    realm_id?: NinchatProperties.RealmID
    tag_id: NinchatProperties.TagID
    tag_attrs: NinchatAttributes.NinchatTagAttributes
}

export interface NinchatQueueStatsContentsEvent extends NinchatEvents {
    event: 'queue_stats_contents'
    action_id: NinchatProperties.ActionID
    queue_stats: NinchatProperties.NinchatQueueStats
}

export interface NinchatIceBegunEvent extends NinchatEvents {
    event: 'ice_begun'
    action_id: NinchatProperties.ActionID
    stun_servers: NinchatProperties.NinchatStunServer[]
    turn_servers: NinchatProperties.NinchatTurnServer[]
}

export interface NinchatSearchResultsEvent extends NinchatEvents {
    event: 'search_results'
    action_id: NinchatProperties.ActionID
    users?: NinchatProperties.NinchatSearchUsers
    channels?: NinchatProperties.NinchatSearchChannels
}

export interface NinchatPongEvent extends NinchatEvents {
    event: 'pong'
    action_id?: NinchatProperties.ActionID
}

export interface NinchatAckEvent extends NinchatEvents {
    event: 'ack'
    action_id: NinchatProperties.ActionID
}

/* Internal API
 * - event order from internal api documentation
 * - event property order from internal api documentation
 */

export interface NinchatCommentCreatedEvent extends NinchatEvents {
    event: 'comment_created'
    action_id?: NinchatProperties.ActionID
    queue_id: NinchatProperties.QueueID
    audience_id: NinchatProperties.AudienceID
    comment_change?: NinchatProperties.CommentChange
    comment_id: NinchatProperties.CommentID
    comment_time: NinchatProperties.Time
    comment_user_id: NinchatProperties.UserID // Indicates the commenter, the assignee, the unassigned user or whodunnit (regardless of who was the assigner)
    comment_text?: NinchatProperties.CommentText
}

export interface NinchatAudienceRangeCommentContentsEvent extends NinchatEvents {
    event: 'audience_range_comment_contents'
    action_id: NinchatProperties.ActionID
    queue_id: NinchatProperties.QueueID
    audience_range_comments: NinchatProperties.NinchatAudienceRangeComments
}

// export interface NinchatMessageSuggestionsEvent extends NinchatEvent {}

export interface NinchatAudienceRegisteredEvent extends NinchatEvents {
    event: 'audience_registered'
    action_id: NinchatProperties.ActionID
    audience_id: NinchatProperties.AudienceID
}

export interface NinchatSsoBegunEvent {
    event: 'sso_begun'
    sso_type: NinchatProperties.SsoType
    sso_name: NinchatProperties.SsoName
    sso_url: NinchatProperties.SsoUrl
}

/* Undocumented API
 * - event order alphabetically
 * - event property order
 *   - id properties first alphabetically
 *   - rest of properties alphabetically
 */

export interface NinchatJitsiDiscoveredEvent extends NinchatEvents {
    event: 'jitsi_discovered'
    action_id: NinchatProperties.ActionID
    jitsi_room: NinchatProperties.JitsiRoom
    jitsi_token: NinchatProperties.JitsiToken
}

export interface NinchatQueueActivityEvent extends NinchatEvents {
    event: 'queue_activity'
    action_id?: NinchatProperties.ActionID
    queue_id: NinchatProperties.QueueID
    queue_activity: NinchatProperties.NinchatQueueActivity[]
}

export interface NinchatTranscriptFoundEvent extends NinchatEvents {
    event: 'transcript_found'
    action_id: NinchatProperties.ActionID
    channel_id?: NinchatProperties.ChannelID // unused
    dialogue_id?: NinchatProperties.DialogueID // unused
    audience_id?: NinchatProperties.AudienceID
    queue_id?: NinchatProperties.QueueID
    realm_id?: NinchatProperties.RealmID
    // https://github.com/somia/nin/pull/2583#issuecomment-1904163696
    complete_time?: NinchatProperties.Time
    assign_user_id?: NinchatProperties.UserID
    transcript_status?: NinchatProperties.TranscriptStatus
}

export interface NinchatTranscriptUpdatedEvent extends NinchatEvents {
    event: 'transcript_updated'
    action_id?: NinchatProperties.ActionID
    audience_id: NinchatProperties.AudienceID
    queue_id?: NinchatProperties.QueueID
    realm_id?: NinchatProperties.RealmID
    complete_time?: NinchatProperties.Time
    assign_user_id?: NinchatProperties.UserID
    transcript_status?: NinchatProperties.TranscriptStatus
}

/* Unions */

export type NinchatSessionEvent =
    // Public API
    NinchatErrorEvent |
    NinchatSessionCreatedEvent

export type NinchatEvent =
    // Public API
    NinchatErrorEvent |
    NinchatSessionUpdatedEvent |
    NinchatSessionStatusUpdatedEvent |
    NinchatUserCreatedEvent |
    NinchatUserFoundEvent |
    NinchatUserUpdatedEvent |
    NinchatUserDeletedEvent |
    NinchatIdentityFoundEvent |
    NinchatIdentityCreatedEvent |
    NinchatIdentityUpdatedEvent |
    NinchatIdentityDeletedEvent |
    NinchatDialogueUpdatedEvent |
    NinchatChannelCreatedEvent |
    NinchatChannelFoundEvent |
    NinchatChannelJoinedEvent |
    NinchatChannelPartedEvent |
    NinchatChannelUpdatedEvent |
    NinchatChannelDeletedEvent |
    NinchatChannelMemberJoinedEvent |
    NinchatChannelMemberPartedEvent |
    NinchatChannelMemberUpdatedEvent |
    NinchatRealmFoundEvent |
    NinchatRealmQueuesFoundEvent |
    NinchatRealmJoinedEvent |
    NinchatRealmPartedEvent |
    NinchatRealmUpdatedEvent |
    NinchatRealmDeletedEvent |
    NinchatRealmMemberJoinedEvent |
    NinchatRealmMemberPartedEvent |
    NinchatRealmMemberUpdatedEvent |
    NinchatQueueCreatedEvent |
    NinchatQueueFoundEvent |
    NinchatQueueUpdatedEvent |
    NinchatQueueDeletedEvent |
    NinchatQueueJoinedEvent |
    NinchatQueuePartedEvent |
    NinchatQueueMemberJoinedEvent |
    NinchatQueueMemberPartedEvent |
    NinchatAudienceEnqueuedEvent |
    NinchatMessageReceivedEvent |
    NinchatMessageUpdatedEvent |
    NinchatHistoryResultsEvent |
    NinchatHistoryDiscardedEvent |
    NinchatAccessFoundEvent |
    NinchatAccessCreatedEvent |
    NinchatMasterKeysFoundEvent |
    NinchatMasterKeyCreatedEvent |
    NinchatMasterKeyDeletedEvent |
    NinchatFileFoundEvent |
    NinchatTranscriptContentsEvent |
    NinchatTranscriptDeletedEvent |
    NinchatQueueMetadataContents |
    NinchatQueueTranscriptsFoundEvent |
    NinchatQueueTranscriptsDeletedEvent |
    NinchatTagCreatedEvent |
    NinchatTagFoundEvent |
    NinchatTagsFoundEvent |
    NinchatTagUpdatedEvent |
    NinchatTagDeletedEvent |
    NinchatQueueStatsContentsEvent |
    NinchatIceBegunEvent |
    NinchatSearchResultsEvent |
    NinchatPongEvent |
    NinchatAckEvent |
    // Internal API
    NinchatCommentCreatedEvent |
    NinchatAudienceRangeCommentContentsEvent |
    // NinchatMessageSuggestionsEvent |
    NinchatAudienceRegisteredEvent |
    NinchatSsoBegunEvent |
    // Undocumented API
    NinchatJitsiDiscoveredEvent |
    NinchatQueueActivityEvent |
    NinchatTranscriptFoundEvent |
    NinchatTranscriptUpdatedEvent |
    NinchatPermissionGroupsEvent |
    NinchatPermissionGroupUpdatedEvent |
    NinchatFileUploadedEvent

// vim:foldmethod=syntax
